import type { FlowComposerDraft } from './types/flow-composer.ts';
import { parseResultComposition } from './types/result-composition.ts';

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

/** Stable JSON, independent of database object key ordering. */
export const stableContractJson = (value: unknown): string => {
  const canonical = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(canonical);
    if (item && typeof item === 'object') return Object.fromEntries(
      Object.entries(item).filter(([, child]) => child !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)).map(([key, child]) => [key, canonical(child)]),
    );
    return item;
  };
  return JSON.stringify(canonical(value));
};

/** Excludes only presentation (labels, tab/section order, preview examples, layout). */
export const getStoredToolDesignContract = (snapshot: Record<string, unknown>): string => {
  const composition = parseResultComposition(snapshot.result_composition);
  const continuation = record(snapshot.continuation_capability);
  return stableContractJson({
    version: 1,
    ...(snapshot.app_data ? { appData: snapshot.app_data } : {}),
    designIntent: snapshot.design_intent ?? null,
    viewType: snapshot.view_type,
    inputs: snapshot.inputs ?? [],
    outputContract: snapshot.output_contract,
    personaKey: snapshot.persona_key,
    continuityContract: snapshot.continuity_contract ?? null,
    continuation: Object.keys(continuation).length ? {
      jobKey: continuation.jobKey,
      acceptsCategories: continuation.acceptsCategories,
      prefillMapping: continuation.prefillMapping,
    } : null,
    sections: composition ? composition.tabs.map((tab) => ({
      id: tab.id, scope: tab.scope,
      sections: tab.sections.map((section) => ({
        id: section.id, role: section.role, viewType: section.viewType,
        required: section.required !== false, source: section.source ?? null,
      })).sort((a, b) => a.id.localeCompare(b.id)),
    })).sort((a, b) => a.id.localeCompare(b.id)) : snapshot.result_composition ?? null,
    dominos: (Array.isArray(snapshot.dominos) ? snapshot.dominos : []).map((value) => {
      const domino = record(value);
      return {
        actionType: domino.action_type, config: domino.config ?? {},
        inputMapping: domino.input_mapping ?? {}, hitlBehavior: domino.hitl_behavior ?? null,
        contextCategories: domino.context_categories ?? [],
      };
    }),
  });
};

export const getDraftToolDesignContract = (draft: FlowComposerDraft): string =>
  getStoredToolDesignContract({
    design_intent: draft.designIntent, app_data: draft.appData,
    view_type: draft.viewType, inputs: draft.inputs, output_contract: draft.outputContract,
    persona_key: draft.personaKey, result_composition: draft.resultComposition,
    continuity_contract: draft.continuityContract, continuation_capability: draft.continuationCapability,
    dominos: draft.dominos.map((domino) => ({
      action_type: domino.actionType, config: domino.config, input_mapping: domino.inputMapping,
      hitl_behavior: domino.hitlBehavior, context_categories: domino.contextCategories,
    })),
  });

export const getReadModelDesignContract = (draft: FlowComposerDraft): string =>
  stableContractJson({ version: 1, designIntent: draft.designIntent ?? null, title: draft.title, description: draft.description, readModel: draft.readModel });

/** A bound composition is the user-visible output contract and owns its test verdict. */
export const isSandboxOutputUsable = (params: {
  hasBoundSections: boolean;
  resultPayloadStatus: unknown;
  executionSummaryStatus: unknown;
}): boolean => {
  if (params.hasBoundSections) return params.resultPayloadStatus === 'complete';
  return params.resultPayloadStatus !== 'incomplete' && params.executionSummaryStatus !== 'invalid';
};

/** Change detector for a design sheet (not a security hash): any edit to the
 * sheet changes it, so a validation applies to one exact sheet only. */
export const designIntentSignature = (intent: unknown): string => {
  const text = stableContractJson(intent ?? null);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${hash.toString(16).padStart(8, '0')}:${text.length}`;
};
